import { useEffect, useState } from 'react'
import { Avatar, Box, Grid, Typography, Chip, Divider } from '@mui/material'
import CreateIcon from '@mui/icons-material/Create';
import { ErrorLogger } from '../../../../utils/Logger';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LanguageIcon from '@mui/icons-material/Language';
import SchoolIcon from '@mui/icons-material/School';
import WorkIcon from '@mui/icons-material/Work';
import FolderIcon from '@mui/icons-material/Folder';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import BusinessIcon from '@mui/icons-material/Business';
import CodeIcon from '@mui/icons-material/Code';
import './SellerProfileDetailsSection.scss'

import { ProfileVisibilityTypeEnum } from '../../../../enums/ProfileVisibilityTypeEnum'
import { SellerInterface } from '../../../../Models/Seller'
import Aos from 'aos';
import { truncateString } from '../../../../utils/uiUtils';
import { SellerAPIHandler } from '../../../../services/NetworkClient/BackendAPIs/SellerAPIhandler';
import { Link } from 'react-router-dom';
import { convertTimeStampToMonthAndYearFormat } from '../../../../utils/DateAndTimeUtils';
import GGPlaceholderImage from '../../../CommonComponents/GGPlaceholderImage/GGPlaceholderImage';
import { CategoryAPIHandler } from '../../../../services/NetworkClient/BackendAPIs/CategoryAPIHandler';
import { SubcategoryAPIHandler } from '../../../../services/NetworkClient/BackendAPIs/SubcategoryAPIhandler';
import { GigAPIHandler } from '../../../../services/NetworkClient/BackendAPIs/GigAPIHandler';
import Gig from '../../../../Models/Gig';
import StoreIcon from '@mui/icons-material/Store';
import { formatCurrencyIndian } from '../../../../utils/MoneyConversionUtils';
import StarIcon from '@mui/icons-material/Star';


interface ProfileDetailsSectionPropTypes {
    visibility: ProfileVisibilityTypeEnum
    sellerId: string
}

const SellerProfileDetailsSection = ({visibility, sellerId} : ProfileDetailsSectionPropTypes) => {

    let [seller, setSeller] = useState<SellerInterface>();  
    const [categoryMap, setCategoryMap] = useState<Record<string, string>>({});
    const [subcategoryMap, setSubcategoryMap] = useState<Record<string, string>>({});
    const [sellerGigs, setSellerGigs] = useState<Gig[]>([]);
    const [isLoadingGigs, setIsLoadingGigs] = useState(false);

    useEffect(() => {
        document.title = "GenieGig | Profile";
        Aos.init({ duration: 1500 });

        // Fetch seller data - make single API call and extract both seller object and createdAt

        SellerAPIHandler.shared.getSellerById(sellerId).then((seller) => {
            setSeller(seller);
        }).catch(err => {
            ErrorLogger.log(err, 'SellerProfileDetailsSection.fetchSeller');
        })

        // Fetch categories and subcategories for mapping IDs to names
        CategoryAPIHandler.shared.getAllCategories(1, 100).then(categoryRes => {
            const categoryMapUtil: Record<string, string> = {};
            categoryRes.categories.forEach((category) => {
                categoryMapUtil[category.id] = category.title;
            });
            setCategoryMap(categoryMapUtil);
        }).catch((err) => { ErrorLogger.log(err, 'SellerProfileDetailsSection.fetchCategories'); });

        SubcategoryAPIHandler.shared.getAllSubcategories(1, 100).then(subcategoryRes => {
            const subcategoryMapUtil: Record<string, string> = {};
            subcategoryRes.subcategories.forEach((subcategory) => {
                subcategoryMapUtil[subcategory.id] = subcategory.name;
            });
            setSubcategoryMap(subcategoryMapUtil);
        }).catch((err) => { ErrorLogger.log(err, 'SellerProfileDetailsSection.fetchSubcategories'); });

        // Fetch seller gigs
        const fetchSellerGigs = async () => {
            setIsLoadingGigs(true);
            try {
                const gigs = await GigAPIHandler.shared.getGigsBySeller(sellerId);
                setSellerGigs(gigs);
            } catch (error) {
                ErrorLogger.log(error, 'SellerProfileDetailsSection.fetchSellerGigs');
            } finally {
                setIsLoadingGigs(false);
            }
        };
        fetchSellerGigs();
    // eslint-disable-next-line
    }, []);

    // Get member since year from createdAt (when user joined platform)
    const getMemberSinceYear = () => {
        if (seller?.createdAt) {
            return seller.createdAt.getFullYear();
        }
        return new Date().getFullYear();
    }

    // Get professional status (occupation)
    const getProfessionalStatus = () => {
        return seller?.occupation || "Professional";
    }

    // Helper functions for category and subcategory names
    const getCategoryName = (categoryId: string) => {
        return categoryMap[categoryId] || categoryId;
    };

    const getSubcategoryName = (subcategoryId: string) => {
        return subcategoryMap[subcategoryId] || subcategoryId;
    };

    // Profile Header - Clean and flat with cover image
    const ProfileHeader = () => {
        return (
            <Box className='profile-header'>
                {/* Cover Image */}
                <Box className="cover-image-container">
                    <GGPlaceholderImage
                        src={seller?.coverImage} 
                        alt={'cover'} 
                        className="cover-image"
                        placeholder={
                            <Box 
                                className='cover-image' 
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)'
                                }}
                            ></Box>
                        }
                    />
                </Box>
                
                <Box className="header-content">
                    <Box className="profile-avatar-section">
                        <Avatar
                            alt={seller?.fullName}
                            src={seller?.profileImage}
                            className="profile-avatar"
                        >
                            <Box sx={{ height: "100%", width: "100%", bgcolor: '#002f6c' }} className="center">
                                <Typography fontSize={'64px'} fontWeight={600}> {seller?.fullName?.[0] || 'U'} </Typography>
                            </Box>
                        </Avatar>
                    </Box>
                    <Box className="profile-info-section">
                        <Box className="profile-name-row">
                            <Typography variant='h4' fontWeight={600} className='profile-name'>{seller?.fullName}</Typography>
                            {visibility === ProfileVisibilityTypeEnum.MyProfile && (
                                <Link to="/profile-edit" className="edit-link">
                                    <CreateIcon className="edit-icon"/>
                                </Link>
                            )}
                        </Box>
                        <Typography variant='h6' fontWeight={400} className='profile-tagline' color="text.secondary">{seller?.tagline}</Typography>
                        <Box className="profile-meta" mt={1}>
                            <Box className="meta-item">
                                <LocationOnIcon sx={{ fontSize: 18, mr: 0.5, color: 'text.secondary' }} />
                                <Typography variant='body2' color="text.secondary">{seller?.location?.state}, {seller?.location?.country}</Typography>
                            </Box>
                            <Box className="meta-item">
                                <BusinessIcon sx={{ fontSize: 18, mr: 0.5, color: 'text.secondary' }} />
                                <Typography variant='body2' color="text.secondary">{getProfessionalStatus()}</Typography>
                            </Box>
                            <Box className="meta-item">
                                <CalendarTodayIcon sx={{ fontSize: 18, mr: 0.5, color: 'text.secondary' }} />
                                <Typography variant='body2' color="text.secondary">Freelancer since {getMemberSinceYear()}</Typography>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        )
    }

    // About Panel
    const AboutPanel = () => {
        let [readMore, setReadMore] = useState(false);
        return (
            <Box className='section-card' data-aos="fade-up">
                <Box className="section-header">
                    <Typography variant='h6' fontWeight={600} className='section-title'>About</Typography>
                </Box>
                <Divider className="section-divider" />
                <Box className="section-content" pt={2}>
                    <Typography variant='body1' className="about-text" sx={{ whiteSpace: 'pre-wrap' }}>
                        { !readMore && truncateString(seller?.personalDescription, window.innerWidth, 100) }
                        {
                            !readMore && seller?.personalDescription !== undefined && (seller.personalDescription.length > truncateString(seller?.personalDescription, window.innerWidth, 100).length ?
                            <span className='read-more-text' onClick={() => setReadMore(true)}> ... Read More </span> : ""
                            )
                        } 
                        {readMore && seller?.personalDescription}
                    </Typography>
                </Box>
            </Box>
        )
    }

    // Skills Panel
    const SkillsPanel = () => {
        // Collect all categories, subcategories, and extraNotes separately
        const allCategories: string[] = [];
        const allSubcategories: string[] = [];
        const allExtraNotes: string[] = [];

        if (seller?.skills && seller.skills.length > 0) {
            seller.skills.forEach((skill) => {
                if (skill.category) {
                    const categoryName = getCategoryName(skill.category);
                    if (!allCategories.includes(categoryName)) {
                        allCategories.push(categoryName);
                    }
                }
                if (skill.subcategory) {
                    const subcategoryName = getSubcategoryName(skill.subcategory);
                    if (!allSubcategories.includes(subcategoryName)) {
                        allSubcategories.push(subcategoryName);
                    }
                }
                if (skill.extraNotes && skill.extraNotes.trim() !== '') {
                    if (!allExtraNotes.includes(skill.extraNotes)) {
                        allExtraNotes.push(skill.extraNotes);
                    }
                }
            });
        }

        return (
            <Box className='section-card' data-aos="fade-up">
                <Box className="section-header">
                    <CodeIcon className="section-icon" />
                    <Typography variant='h6' fontWeight={600} className='section-title'>Skills</Typography>
                </Box>
                <Divider className="section-divider" />
                <Box className="section-content" pt={2}>
                    {seller?.skills && seller.skills.length > 0 ? (
                        <>
                            {/* All Categories */}
                            {allCategories.length > 0 && (
                                <Box display="flex" flexWrap="wrap" gap={1} mb={allSubcategories.length > 0 || allExtraNotes.length > 0 ? 2 : 0}>
                                    {allCategories.map((categoryName, index) => (
                                        <Chip 
                                            key={index}
                                            label={categoryName}
                                            className="skill-chip"
                                        />
                                    ))}
                                </Box>
                            )}
                            
                            {/* All Subcategories */}
                            {allSubcategories.length > 0 && (
                                <Box display="flex" flexWrap="wrap" gap={1} mb={allExtraNotes.length > 0 ? 2 : 0}>
                                    {allSubcategories.map((subcategoryName, index) => (
                                        <Chip 
                                            key={index}
                                            label={subcategoryName}
                                            className="skill-chip"
                                        />
                                    ))}
                                </Box>
                            )}
                            
                            {/* All Extra Notes */}
                            {allExtraNotes.length > 0 && (
                                <Box>
                                    {allExtraNotes.map((note, index) => (
                                        <Typography 
                                            key={index}
                                            variant='body2' 
                                            color="text.secondary" 
                                            sx={{ fontStyle: 'italic', mt: index > 0 ? 1 : 0 }}
                                        >
                                            #{note}
                                        </Typography>
                                    ))}
                                </Box>
                            )}
                        </>
                    ) : (
                        <Typography variant='body2' color="text.secondary">No skills added</Typography>
                    )}
                </Box>
            </Box>
        )
    }


    // Languages Panel
    const LanguagesPanel = () => {
        return (
            <Box className='section-card' data-aos="fade-up">
                <Box className="section-header">
                    <LanguageIcon className="section-icon" />
                    <Typography variant='h6' fontWeight={600} className='section-title'>Languages</Typography>
                </Box>
                <Divider className="section-divider" />
                <Box className="section-content" pt={2}>
                    {seller?.location?.languages && seller.location.languages.length > 0 ? (
                        <Box display="flex" flexWrap="wrap" gap={1}>
                            {seller.location.languages.map((language, index) => (
                                <Chip 
                                    key={index}
                                    label={language} 
                                    className="language-chip"
                                    variant="outlined"
                                />
                            ))}
                        </Box>
                    ) : (
                        <Typography variant='body2' color="text.secondary">No languages specified</Typography>
                    )}
                </Box>
            </Box>
        )
    }

    // Education Panel
    const EducationPanel = () => {
        return (
            <Box className='section-card' data-aos="fade-up">
                <Box className="section-header">
                    <SchoolIcon className="section-icon" />
                    <Typography variant='h6' fontWeight={600} className='section-title'>Education</Typography>
                </Box>
                <Divider className="section-divider" />
                <Box className="section-content" pt={2}>
                    {seller?.education && seller.education.length > 0 ? (
                        seller.education.map((edu, index, array) => (
                            <Box key={index} className="education-item" mb={index < array.length - 1 ? 3 : 0}>
                                <Typography variant='subtitle1' fontWeight={600} mb={0.5}>
                                    {edu.degree}
                                </Typography>
                                <Typography variant='body2' color="text.secondary" mb={1}>
                                    {edu.institute}
                                </Typography>
                                {edu.passoutYear && (
                                    <Chip 
                                        label={`Graduated ${edu.passoutYear}`}
                                        className="graduation-chip"
                                        size="small"
                                        variant="outlined"
                                    />
                                )}
                            </Box>
                        ))
                    ) : (
                        <Typography variant='body2' color="text.secondary">No education information available</Typography>
                    )}
                </Box>
            </Box>
        )
    }

    // Work Experience Panel
    const WorkExperiencePanel = () => {
        let [seeMore, setSeeMore] = useState(false);

        return (
            <Box className='section-card' data-aos="fade-left">
                <Box className="section-header">
                    <WorkIcon className="section-icon" />
                    <Typography variant='h6' fontWeight={600} className='section-title'>Work Experience</Typography>
                </Box>
                <Divider className="section-divider" />
                <Box className="section-content" pt={2}>
                    {seller?.workExperience && seller.workExperience.length > 0 ? (() => {
                        const workExperience = seller.workExperience;
                        const displayedExperiences = workExperience.slice(0, !seeMore ? 2 : undefined);
                        const totalLength = workExperience.length;
                        const displayedLength = seeMore ? totalLength : Math.min(2, totalLength);
                        
                        return (
                            <>
                                {displayedExperiences.map((experience, index) => (
                                    <Box key={index} className="experience-item" mb={index < displayedLength - 1 ? 3 : 0}>
                                        <Box display="flex" alignItems="flex-start">
                                            <Box className="company-icon">
                                                {experience.company?.[0]?.toUpperCase() || 'C'}
                                            </Box>
                                            <Box ml={2} flex={1}>
                                                <Typography variant='subtitle2' fontWeight={600} mb={0.5}>
                                                    {experience.designation}
                                                </Typography>
                                                <Typography variant='body2' color="text.secondary" mb={0}>
                                                    {experience.company}
                                                </Typography>
                                                <Chip 
                                                    label={experience.employmentType}
                                                    className="employment-type-chip"
                                                    size="small"
                                                    variant="outlined"
                                                />
                                                <Box display="flex" alignItems="center" mt={1.5}>
                                                    <CalendarTodayIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                                                    <Typography variant='body2' color="text.secondary">
                                                        {convertTimeStampToMonthAndYearFormat(new Date(experience.startDate), 'short')} - {experience.endDate ? convertTimeStampToMonthAndYearFormat(new Date(experience.endDate), 'short') : "Present"}
                                                        {experience.endDate && (() => {
                                                            const start = new Date(experience.startDate);
                                                            const end = new Date(experience.endDate);
                                                            const months = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24 * 30));
                                                            return <> • {months} month{months !== 1 ? 's' : ''}</>;
                                                        })()}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Box>
                                ))}
                                {totalLength > 2 && (
                                    <Box display="flex" justifyContent="flex-end" mt={2}>
                                        <button className='see-more-button' onClick={() => setSeeMore(!seeMore)}>
                                            {!seeMore ? 'See More' : 'See Less'}
                                        </button>
                                    </Box>
                                )}
                            </>
                        );
                    })() : (
                        <Typography variant='body2' color="text.secondary">No work experience added</Typography>
                    )}
                </Box>
            </Box>
        )
    }

    // Gigs Panel
    const GigsPanel = () => {
        return (
            <Box className='section-card' data-aos="fade-up">
                <Box className="section-header">
                    <StoreIcon className="section-icon" />
                    <Typography variant='h6' fontWeight={600} className='section-title'>Gigs</Typography>
                </Box>
                <Divider className="section-divider" />
                <Box className="section-content" pt={2}>
                    {isLoadingGigs ? (
                        <Typography variant='body2' color="text.secondary">Loading gigs...</Typography>
                    ) : sellerGigs.length === 0 ? (
                        <Typography variant='body2' color="text.secondary">No gigs listed yet</Typography>
                    ) : (
                        <Box className="gigs-grid-container gigListSection">
                            {sellerGigs.map((gig) => (
                                <Link
                                key={gig.id}
                                to={"/gig/" + gig.id}
                                >        
                                    <div className='gig-card'>
                                        <div className='gig-card-image'>
                                            <img src={gig.coverImg} alt={gig.title} />
                                        </div>
                                        <div className='gig-card-content'>
                                            <Typography className='gig-title' variant='subtitle1' fontWeight={600} component="div" sx={{ width: '100%' }}>{gig.title}</Typography>
                                            <Typography variant='body2' color="text.secondary">Starts from <strong style={{ color: '#EF4D00' }}>{formatCurrencyIndian(gig.minPrice * 100)}</strong></Typography>
                                            <div style={{display:'flex', alignItems:'center', gap: '4px', marginTop: '4px'}}>
                                                <StarIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                                <Typography variant='body2'>{gig.rating === undefined ? '0.0' : gig.rating}</Typography>
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </Box>
                    )}
                </Box>
            </Box>
        )
    }

    // Projects Panel
    const ProjectsPanel = () => {
        let [seeMore, setSeeMore] = useState(false);

        // Collect all projects from all skills with their subcategory info
        const getAllProjects = () => {
            if (!seller?.skills || seller.skills.length === 0) return [];
            
            const allProjects: Array<{
                project: any;
                subcategoryName: string;
                categoryName: string;
            }> = [];
            
            seller.skills.forEach((skill) => {
                if (skill.projects && skill.projects.length > 0) {
                    skill.projects.forEach((project) => {
                        allProjects.push({
                            project,
                            subcategoryName: getSubcategoryName(skill.subcategory),
                            categoryName: getCategoryName(skill.category)
                        });
                    });
                }
            });
            
            return allProjects;
        };

        const allProjects = getAllProjects();

        return (
            <Box className='section-card' data-aos="fade-up">
                <Box className="section-header">
                    <FolderIcon className="section-icon" />
                    <Typography variant='h6' fontWeight={600} className='section-title'>Projects</Typography>
                </Box>
                <Divider className="section-divider" />
                <Box className="section-content" pt={2}>
                    {allProjects.length > 0 ? (
                        <>
                            {allProjects.slice(0, !seeMore ? 3 : undefined).map((item, index) => (
                                <Box key={index} className="project-item" mb={index < (seeMore ? allProjects.length : Math.min(3, allProjects.length)) - 1 ? 3 : 0}>
                                    <Box flex={1}>
                                            <Box display="flex" alignItems="center" gap={1} mb={0.5} flexWrap="wrap">
                                                <Typography variant='subtitle1' fontWeight={600}>
                                                    {item.project.projectTitle}
                                                </Typography>
                                                {item.subcategoryName && (
                                                    <Chip 
                                                        label={item.subcategoryName}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{ fontSize: '0.7rem', height: '20px' }}
                                                    />
                                                )}
                                            </Box>
                                            <Typography variant='body2' color="text.secondary" mb={1}>
                                                {item.project.projectDescription}
                                            </Typography>
                                            <Box display="flex" alignItems="center" mb={item.project.projectLink ? 1 : 0}>
                                                <CalendarTodayIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                                                <Typography variant='body2' color="text.secondary">
                                                    {convertTimeStampToMonthAndYearFormat(new Date(item.project.startDate), 'short')} - {convertTimeStampToMonthAndYearFormat(new Date(item.project.endDate), 'short')}
                                                </Typography>
                                            </Box>
                                            {item.project.projectLink && (
                                                <Box mt={1}>
                                                    <Typography variant='body2' color="text.secondary" sx={{ fontWeight: 500, mb: 0.5 }}>
                                                        Link:
                                                    </Typography>
                                                    <Typography 
                                                        variant='body2' 
                                                        component="a"
                                                        href={item.project.projectLink}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        sx={{ 
                                                            color: 'primary.main',
                                                            textDecoration: 'none',
                                                            wordBreak: 'break-all',
                                                            '&:hover': { textDecoration: 'underline' }
                                                        }}
                                                    >
                                                        {item.project.projectLink}
                                                    </Typography>
                                                </Box>
                                            )}
                                    </Box>
                                </Box>
                            ))}
                            {allProjects.length > 3 && (
                                <Box display="flex" justifyContent="center" mt={2}>
                                    <button className='see-more-button' onClick={() => setSeeMore(!seeMore)}>
                                        {!seeMore ? 'See More' : 'See Less'}
                                    </button>
                                </Box>
                            )}
                        </>
                    ) : (
                        <Typography variant='body2' color="text.secondary">No projects added</Typography>
                    )}
                </Box>
            </Box>
        )
    }
    
  return (
    <div className='new-profile-details-section'>
        <div className='new-profile-details-section-container'>
            <ProfileHeader />
            <Grid container spacing={3} className="content-grid">
                {/* Left Column */}
                <Grid item xs={12} md={4} className="left-column">
                    <AboutPanel />
                    <SkillsPanel />
                    <LanguagesPanel />
                    <EducationPanel />
                </Grid>
                
                {/* Right Column */}
                <Grid item xs={12} md={8} className="right-column">
                    <WorkExperiencePanel />
                    <ProjectsPanel />
                    <GigsPanel />
                </Grid>
            </Grid>
        </div>
    </div>
  )
}


export default SellerProfileDetailsSection

